import { Injectable, inject, signal } from '@angular/core';
import { finalize, Observable, tap, throwError } from 'rxjs';
import {
  TicketParseStrategy,
  TicketProcessingResponseDto,
} from '@features/client/models/ticket-processing-model';
import { TicketProcessingApiService } from './ticket-processing-api-service';

@Injectable({ providedIn: 'root' })
export class PublicBillSplitTicketFacade {
  private api = inject(TicketProcessingApiService);

  readonly selectedImage = signal<Blob | File | null>(null);
  readonly ticketImageUrl = signal<string | null>(localStorage.getItem('dinno_ticket_image_url'));
  readonly lastProcessingId = signal<string | null>(
    localStorage.getItem('dinno_last_processing_id'),
  );
  readonly isProcessingScan = signal(false);
  readonly scanWarning = signal<{ read: number; calculated: number } | null>(null);

  setSelectedTicketImage(image: Blob | File | null): void {
    this.selectedImage.set(image);
  }

  clearScanWarning(): void {
    this.scanWarning.set(null);
  }

  processTicket(splitId: string): Observable<TicketProcessingResponseDto> {
    const image = this.selectedImage();

    if (!image) {
      return throwError(() => new Error('No ticket image selected'));
    }

    this.isProcessingScan.set(true);

    return this.api.processTicket(splitId, image).pipe(
      tap((response) => this.handleTicketResponse(response)),
      finalize(() => this.isProcessingScan.set(false)),
    );
  }

  reprocessTicket(strategy?: TicketParseStrategy): Observable<TicketProcessingResponseDto> {
    const processingId = this.lastProcessingId();
    if (!processingId) {
      return throwError(() => new Error('No processing id found'));
    }

    this.isProcessingScan.set(true);

    return this.api.reprocessTicket(processingId, strategy).pipe(
      tap((response) => this.handleTicketResponse(response)),
      finalize(() => this.isProcessingScan.set(false)),
    );
  }

  private handleTicketResponse(response: TicketProcessingResponseDto): void {
    this.lastProcessingId.set(response.publicId);
    localStorage.setItem('dinno_last_processing_id', response.publicId);

    if (response.cloudinaryUrl) {
      this.ticketImageUrl.set(response.cloudinaryUrl);
      localStorage.setItem('dinno_ticket_image_url', response.cloudinaryUrl);
    }

    if (
      !response.isTotalValid &&
      response.extractedTotal != null &&
      response.calculatedTotal != null
    ) {
      this.scanWarning.set({
        read: response.extractedTotal,
        calculated: response.calculatedTotal,
      });
    } else {
      this.scanWarning.set(null);
    }
  }

  clear(): void {
    this.selectedImage.set(null);
    this.ticketImageUrl.set(null);
    this.lastProcessingId.set(null);
    this.scanWarning.set(null);

    localStorage.removeItem('dinno_ticket_image_url');
    localStorage.removeItem('dinno_last_processing_id');
  }
}
